import React, { useEffect, useContext, useState } from "react";
import Footer from "../components/footer";
import { listOrganizationalUnits } from "../services/api_org_unit";
import { getWorkingAreaList } from "../services/api_working_area";
import { AuthContext } from "../contexts/auth";
import ComboboxAutocomplete from "../components/combobox_autocomplete";
import Fetching from "../components/fetching";
import { Link } from "wouter";
import CustomDatePicker from "../components/custom_date_picker";
import CollapsibleTable from "../components/collapsible_table";
import moment from "moment";
import PopoverComponentCustom from "../components/popover_component";
import StatisticsViewControls from "../components/statistics_view_control";
import { AdjustmentsHorizontalIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import CollapsibleWorkingAreaTable from "../components/collapsible_working_area_table";

function Statistics() {
    const { token, profile } = useContext(AuthContext);
    const [selectedUnitId, setSelectedUnitId] = useState(null);
    const [units, setUnits] = useState([]);
    const [unitData, setUnitData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [viewMode, setViewMode] = useState("organizational_units");
    const [workingAreas, setWorkingAreas] = useState([]);
    const [selectedWorkingAreaId, setSelectedWorkingAreaId] = useState(null);
    const [isLoadingAreas, setIsLoadingAreas] = useState(false);

    const isProfileComplete = profile.has_complete_profile;

    const NO_UNIT = {
        id: 0,
        name: ":: Senza Unità ::",
    };

    const selectedUnit = selectedUnitId !== null && selectedUnitId !== undefined ? units.find((unit) => Number(unit.id) === Number(selectedUnitId)) : null;
    const selectedWorkingArea = selectedWorkingAreaId !== null && selectedWorkingAreaId !== undefined ? workingAreas.find((area) => Number(area.id) === Number(selectedWorkingAreaId)) : null;

    const canViewWorkingAreas =
        profile.customer_type !== "employee" ||
        profile.employee?.permissions?.includes("hr.view_working_area_all") ||
        profile.employee?.permissions?.includes("hr.view_working_area_ou");
    
    const canManageWorkingAreas =
        profile.customer_type !== "employee" ||
        profile.employee?.permissions?.includes("hr.manage_working_area_all") ||
        profile.employee?.permissions?.includes("hr.manage_working_area_ou");

    useEffect(() => {
        const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
        const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");

        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
    }, []);

    useEffect(() => {
        if (!token) {
            return;
        }

        setIsLoading(true);
        setIsError(false);

        listOrganizationalUnits(token, true, null, 'shifts').then((data) => {
            setUnits([
                ...(data.data || []),
                NO_UNIT,
            ]);
            setIsLoading(false);
        }).catch(error => {
            console.error('Error fetching units:', error);
            if (error?.response?.status !== 402) {
                setIsError(true);
            }
            setIsLoading(false);
        });
    }, [token]);

    useEffect(() => {
        if (!token || viewMode !== "working_areas" || workingAreas.length > 0) {
            return;
        }

        setIsLoadingAreas(true);
        setIsError(false);

        getWorkingAreaList(token, {
            status: "all",
            paginate: false,
        }, 'shifts')
            .then((response) => {
                const areas = Array.isArray(response)
                    ? response
                    : response?.results || [];

                setWorkingAreas(areas);
                setIsLoadingAreas(false);
            })
            .catch((error) => {
                console.error("Errore caricamento aree di lavoro:", error);
                setIsError(true);
                setIsLoadingAreas(false);
            });
    }, [token, viewMode, workingAreas.length]);

    const handleViewModeChange = (newMode) => {
        setViewMode(newMode);
        setSelectedUnitId(null);
        setSelectedWorkingAreaId(null);
        setUnitData(null);
    };

    const handleParentChange = (selectedParent) => {
        if (selectedParent !== null && selectedParent !== undefined) {
            setSelectedUnitId(selectedParent.id);
        } else {
            setSelectedUnitId(null);
            setUnitData(null);
        }
    };

    const handleWorkingAreaChange = (selectedArea) => {
        setSelectedWorkingAreaId(selectedArea?.id ?? null);
    };

    const handleResetUnitSelection = () => {
        setSelectedUnitId(null);
    };

    const handleResetWorkingAreaSelection = () => {
        setSelectedWorkingAreaId(null);
    };

    const handleDateChange = (newStartDate, newEndDate) => {
        const formattedStartDate = newStartDate ? moment(newStartDate).format("YYYY-MM-DD") : null;
        const formattedEndDate = newEndDate ? moment(newEndDate).format("YYYY-MM-DD") : null;

        setStartDate(formattedStartDate);
        setEndDate(formattedEndDate);
    };

    if (isLoading) return <Fetching />
    if (isError) return <div>Error loading data</div>;

    return (
        <div className="flex flex-col min-h-screen">
            <PopoverComponentCustom title="Statistiche" />
            {profile?.customer_type === "company" && !isProfileComplete && profile?.company?.email == profile?.the_real_user_email && (
                <div className="w-full">
                    <div className="bg-orange-100 text-orange-600 p-4 mb-6">
                        <p className="text-center text-sm font-semibold">
                            Il profilo non è completo. <br />
                            Compila i campi mancanti{' '}
                            <Link
                            to="/profilo/fatturazione"
                            className="text-indigo-600 hover:text-indigo-800 underline"
                            >
                            cliccando qui
                            </Link>
                        </p>
                    </div>
                </div>
            )}
            <main className="flex-grow mt-10 pb-8">
                <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">

                    <div className="mb-6">
                        <StatisticsViewControls
                            mode={viewMode}
                            onModeChange={handleViewModeChange}
                            canViewWorkingAreas={canViewWorkingAreas}
                            canManageWorkingAreas={canManageWorkingAreas}
                        />
                    </div>

                    <section className="overflow-visible rounded-t-xl border-t border-x border-gray-200 bg-white shadow-sm">
                        <div className="flex items-center gap-3 border-b border-gray-100 bg-indigo-50/50 px-4 py-3 sm:px-5">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-indigo-600 ring-1 ring-indigo-100">
                                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                            </div>

                            <div>
                                <h2 className="text-sm font-semibold uppercase text-indigo-800">
                                    Filtri statistiche
                                </h2>
                                <p className="mt-0.5 text-xs text-gray-500">
                                    Seleziona i filtri da applicare alla visualizzazione
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-5 px-4 pt-5 sm:px-5 xl:grid-cols-2 xl:items-end pb-3">

                            <div className="min-w-0 w-full">
                                <div className="mb-1.5 flex items-center justify-between gap-3">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                                        {viewMode === "organizational_units"
                                            ? "Unità organizzativa"
                                            : "Area di lavoro"}
                                    </label>

                                    {viewMode === "organizational_units" && selectedUnitId !== null && (
                                        <button
                                            type="button"
                                            onClick={handleResetUnitSelection}
                                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
                                        >
                                            Reset
                                        </button>
                                    )}

                                    {viewMode === "working_areas" && selectedWorkingAreaId !== null && (
                                        <button
                                            type="button"
                                            onClick={handleResetWorkingAreaSelection}
                                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
                                        >
                                            Reset
                                        </button>
                                    )}
                                </div>

                                {viewMode === "organizational_units" ? (
                                    <ComboboxAutocomplete
                                        options={units}
                                        bgActiveColor="bg-indigo-100"
                                        textActiveColor="text-gray-700"
                                        placeholder="Seleziona Unità Organizzativa..."
                                        onChange={handleParentChange}
                                        value={selectedUnit}
                                        positionOptions="absolute"
                                        zIndex="z-[99999]"
                                        className="w-full min-w-0"
                                        borderClass="border-gray-300 rounded-md"
                                        displayValue={(unit) => unit?.name || ""}
                                        renderOption={(unit) => (
                                            <span className="block max-w-full truncate text-sm text-gray-700" title={unit.name}>
                                                {unit.name}
                                            </span>
                                        )}
                                    />
                                ) : isLoadingAreas ? (
                                    <div className="inline-block align-middle border border-dashed rounded-md border-gray-300 text-gray-500 py-2 italic font-normal text-md w-full px-2">
                                        Caricamento...
                                    </div>
                                ) : (
                                    <ComboboxAutocomplete
                                        options={workingAreas}
                                        bgActiveColor="bg-indigo-100"
                                        textActiveColor="text-gray-700"
                                        placeholder="Seleziona Area di Lavoro..."
                                        onChange={handleWorkingAreaChange}
                                        value={selectedWorkingArea}
                                        positionOptions="absolute"
                                        zIndex="z-[99999]"
                                        className="w-full min-w-0"
                                        borderClass="border-gray-300 rounded-md"
                                        displayValue={(area) => area?.name || ""}
                                        renderOption={(area) => (
                                            <div className="flex min-w-0 items-center justify-between gap-3">
                                                <span className="truncate text-sm text-gray-700" title={area.name}>
                                                    {area.name}
                                                </span>

                                                {area.justification_code && (
                                                    <span className="shrink-0 rounded-md bg-indigo-200 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                                                        {area.justification_code}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </div>

                            <div className="min-w-0">
                                <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">
                                    <CalendarDaysIcon className="h-4 w-4 text-indigo-600" />
                                    Seleziona il periodo
                                </label>

                                <div className="rounded-md">
                                    <CustomDatePicker
                                        onDateChange={handleDateChange}
                                        valueStart={startDate}
                                        valueEnd={endDate}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    <div>
                        {viewMode === "organizational_units" ? (
                            <CollapsibleTable
                                selectedUnitId={selectedUnitId}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        ) : (
                            <CollapsibleWorkingAreaTable
                                selectedWorkingAreaId={selectedWorkingAreaId}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        )}
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default Statistics;
